import React, { useEffect, useMemo, useState } from 'react';
import {
  Column,
  Content,
  Dialog,
  Divider,
  Flex,
  Link,
  ProgressCircle,
  Row,
  Cell,
  TableBody,
  TableHeader,
  TableView,
  TextField,
  View,
} from '@adobe/react-spectrum';
import { useDebounce } from '@uidotdev/usehooks';
import { ToastQueue } from '@adobe/react-spectrum';
import { useTranslation } from 'react-i18next';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { GetContactListRequest } from '../../../../services/soap/main/requests/GetContactListRequest';
import { CreateCompanyPersonRelationRequest } from '../../../../services/soap/main/requests/CreateCompanyPersonRelationRequest';
import styles from '../../../../components/protected/layout-components/ExternalContacts/component/ExternalContactsSearch.module.css';

type RelationMode = 'employee' | 'employer';

interface ContactRelationSearchDialogProps {
  contact: any;
  employees: any[];
  mode: RelationMode;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

const PAGE_SIZE = 50;

const ContactRelationSearchDialog: React.FC<ContactRelationSearchDialogProps> = ({
  contact,
  employees,
  mode,
  onClose,
  onSaved,
}) => {
  const { mainService, store } = useDependency();
  const { t } = useTranslation('contacts');
  const [showLoader, setShowLoader] = useState(false);
  const [filterTerm, setFilterTerm] = useState('');
  const [contactRecord, setContactRecord] = useState<any[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<any>>(new Set());
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const debouncedFilterTerm = useDebounce(filterTerm, 700);

  const contactType = mode === 'employee' ? '1' : '2';
  const title =
    mode === 'employee'
      ? t('contact_detail.add_employee', { defaultValue: 'Add employee' })
      : t('contact_detail.add_employer', { defaultValue: 'Add employer' });

  const nameColumn = t('name', { ns: 'layout_components', defaultValue: 'Name' });
  const emailColumn = t('email', { ns: 'layout_components', defaultValue: 'Email' });
  const phoneColumn = t('phone', { ns: 'layout_components', defaultValue: 'Phone' });
  const addLabel = t('add_contact', { ns: 'layout_components', defaultValue: 'Add contact' });
  const cancelLabel = t('cancel', { ns: 'layout_components', defaultValue: 'Cancel' });
  const searchLabel = t('search', { ns: 'layout_components', defaultValue: 'Search' });

  useEffect(() => {
    let cancelled = false;

    const loadContacts = async () => {
      setShowLoader(true);
      try {
        const response: any = await mainService.getContactList(
          new GetContactListRequest(store.Server, store.SessionId, {
            type: contactType,
            limit: PAGE_SIZE,
            offset: 0,
            ...(debouncedFilterTerm ? { contains: `*${debouncedFilterTerm}` } : {}),
          })
        );

        if (cancelled) return;

        if (response?.result === 'OK' && Array.isArray(response?.CONTACT)) {
          const records = response.CONTACT
            .filter((item: any) => String(item?.id || '') !== String(contact?.id || ''))
            .map((item: any) => ({
              ...item,
              name: [item?.name1, item?.name3].filter(Boolean).join(' ').trim(),
              email: item?.email || '',
              phone: item?.phone || '',
            }));

          setContactRecord(records);
        } else {
          setContactRecord([]);
        }
      } finally {
        if (!cancelled) {
          setShowLoader(false);
        }
      }
    };

    void loadContacts();

    return () => {
      cancelled = true;
    };
  }, [contact?.id, contactType, debouncedFilterTerm, mainService, store.Server, store.SessionId]);

  const existingRelationKeys = useMemo(() => {
    return new Set(
      (employees || []).map((relation: any) => {
        const relationCompany = String(relation?.company || '');
        const relationContact = String(relation?.contact || '');
        return `${relationCompany}::${relationContact}`;
      })
    );
  }, [employees]);

  const handleSelectionChange = (keys: any) => {
    setSelectedKeys(keys);
    const selectedId = Array.from(keys || [])[0];
    const row = contactRecord.find((item: any) => String(item.id) === String(selectedId));
    setSelectedRow(row || null);
  };

  const handleAddRelation = async () => {
    if (!selectedRow?.id) return;

    const relationPayload =
      mode === 'employee'
        ? {
            company: contact.id,
            contact: selectedRow.id,
            department: '',
            position: '',
            positionInfo: '',
            isPrimary: '0',
            validFrom: '',
            validTo: '',
          }
        : {
            company: selectedRow.id,
            contact: contact.id,
            department: '',
            position: '',
            positionInfo: '',
            isPrimary: '0',
            validFrom: '',
            validTo: '',
          };

    const relationKey = `${String(relationPayload.company || '')}::${String(relationPayload.contact || '')}`;
    if (existingRelationKeys.has(relationKey)) {
      ToastQueue.info(
        t('contact_detail.relation_already_exists', {
          defaultValue: 'This relation already exists.',
        }),
        { timeout: 3000 }
      );
      return;
    }

    setShowLoader(true);
    try {
      const result = await mainService.createCompanyPersonRelation(
        new CreateCompanyPersonRelationRequest(store.Server, store.SessionId, relationPayload as any)
      );

      if (result?.result === 'OK') {
        ToastQueue.positive(
          t('contact_detail.relation_saved', {
            defaultValue: 'Relation saved successfully.',
          }),
          { timeout: 3000 }
        );
        await onSaved();
        onClose();
      } else {
        ToastQueue.negative(result?.EXCEPTION?.message || t('save_failed', { ns: 'layout_components', defaultValue: 'Save failed' }), {
          timeout: 5000,
        });
      }
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <Dialog width="900px" aria-label={title}>
      <Content>
        <Flex direction="column" gap="size-200">
          <View>
            <Flex direction="column" gap="size-100">
              <View>{searchLabel}</View>
              <Flex
                direction="row"
                alignItems="center"
                justifyContent="end"
                gap="size-100"
                UNSAFE_className={styles.contact_search_child}
              >
                <TextField
                  aria-label={searchLabel}
                  value={filterTerm}
                  onChange={setFilterTerm}
                  width="100%"
                  UNSAFE_className={styles.contact_search_field}
                />
                <i className={`${styles.icon_search} bi bi-search`}></i>
              </Flex>
            </Flex>
          </View>

          <View>
            <TableView
              aria-label={title}
              selectionMode="single"
              selectionStyle="highlight"
              selectedKeys={selectedKeys}
              onSelectionChange={handleSelectionChange}
              density="compact"
              width="100%"
              minHeight="180px"
              maxHeight="420px"
              renderEmptyState={() => (
                <div>{t('no_contact_found', { ns: 'layout_components', defaultValue: 'No contact found.' })}</div>
              )}
            >
              <TableHeader>
                <Column>{nameColumn}</Column>
                <Column>{emailColumn}</Column>
                <Column>{phoneColumn}</Column>
              </TableHeader>
              <TableBody items={contactRecord}>
                {(item: any) => (
                  <Row>
                    <Cell>{item.name || ''}</Cell>
                    <Cell>{item.email || ''}</Cell>
                    <Cell>{item.phone || ''}</Cell>
                  </Row>
                )}
              </TableBody>
            </TableView>
          </View>

          {showLoader && (
            <Flex justifyContent="center" alignItems="center">
              <ProgressCircle aria-label="Loading" isIndeterminate />
            </Flex>
          )}

          <Divider size="S" />

          <Flex justifyContent="end" gap="size-200">
            <Flex UNSAFE_className={styles.contact_search_cancel}>
              <Link isQuiet UNSAFE_className={styles.contact_search_cancel_text} onPress={onClose}>
                {cancelLabel}
              </Link>
            </Flex>
            <Flex
              UNSAFE_className={styles.contact_search_add}
              UNSAFE_style={!selectedRow?.id || showLoader ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
            >
              <Link isQuiet UNSAFE_className={styles.contact_search_add_text} onPress={handleAddRelation}>
                {addLabel}
              </Link>
            </Flex>
          </Flex>
        </Flex>
      </Content>
    </Dialog>
  );
};

export default ContactRelationSearchDialog;
